import { Navigate, Route, Routes } from "react-router-dom";
import { Fragment, useEffect } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { ThemeProvider, useTheme } from "./contexts/ThemeContext";
import { Toaster } from "sonner";
import useDisableHoverOnScroll from "./hooks/useDisableHoverOnScroll.js";
import { fetchUserInfo } from "./store/slices/user/asyncThunk.js";
import {
  selectLoggedIn,
  setLoggedIn,
  setAuthLoading,
} from "./store/slices/auth/slice.js";
import VerifyEmailPage from "./screens/Verify/VerifyEmail.js";
import TermsPage from "./screens/TermsPage/TermsPage.js";
import PrivacyPage from "./screens/PrivacyPage/PrivacyPage.js";
import DeleteAccountPage from "./screens/DeleteAccountPage/DeleteAccountPage.js";
import HomePage from "./screens/HomePage/HomePage.js";
import SubscriptionPage from "./screens/SubscriptionPage/SubscriptionPage.js";
import ReferralEarningsPage from "./screens/ReferralEarningsPage/ReferralEarningsPage.js";
import CollectionPage from "./screens/CollectionPage/CollectionPage.js";
import CollectionDetailsPage from "./screens/CollectionDetailsPage/CollectionDetailsPage.js";
import SeoLandingPage from "./screens/SeoLandingPage/SeoLandingPage.js";
import SeoArticlePage from "./screens/SeoArticlePage/SeoArticlePage.js";
import SeoArticlesPage from "./screens/SeoArticlesPage/SeoArticlesPage.js";
import TelegramPaymentGuidePage from "./screens/TelegramPaymentGuidePage/TelegramPaymentGuidePage.js";
import GlobalStyles from "./styles/globalStyles";
import "./styles/index.css";

import ProtectedRoute from "./components/utils/ProtectedRoute.js";
import { getOrCreateBrowserId } from "./utils/utils.js";
import { selectUser } from "./store/slices/user/slice.js";
import { VIDEO_RESOLUTION } from "./utils/constants.js";

import { ensureTelegramWidgetHost } from "./utils/telegramWidget.js";
import { landingPages } from "./seo/landingPages.js";
import { seoArticles } from "./seo/articles.js";

import { bouncy } from "ldrs";
import "ldrs/react/Bouncy.css";

bouncy.register();

const AppContent = () => {
  const dispatch = useDispatch();
  const loggedIn = useSelector(selectLoggedIn);
  const user = useSelector(selectUser);
  const { theme } = useTheme();
  useDisableHoverOnScroll();

  useEffect(() => {
    if (loggedIn === null) {
      dispatch(fetchUserInfo())
        .unwrap()
        .then(() => {})
        .catch(() => {
          dispatch(setLoggedIn({ loggedIn: false }));
        })
        .finally(() => {
          dispatch(setAuthLoading(false));
        });
    }
  }, [dispatch, loggedIn]);

  useEffect(() => {
    getOrCreateBrowserId();
  }, []);

  useEffect(() => {
    ensureTelegramWidgetHost();
  }, []);

  useEffect(() => {
    if (loggedIn) {
      if (
        user?.subscription &&
        user?.subscription?.name &&
        user?.subscription?.endDate &&
        new Date(user?.subscription?.endDate) > new Date()
      ) {
        const resolution = localStorage.getItem("videoResolution");
        const fps = localStorage.getItem("videoFps");
        const settingsAlreadyReset = localStorage.getItem(
          "settingsAlreadyReset",
        );

        if (!settingsAlreadyReset) {
          if (!resolution || resolution === VIDEO_RESOLUTION.LOW) {
            localStorage.setItem("videoResolution", VIDEO_RESOLUTION.MEDIUM);
          }

          if (!fps || parseInt(fps) === 30) {
            localStorage.setItem("videoFps", 45);
          }
          localStorage.setItem("settingsAlreadyReset", true);
        }
      }
    }
  }, [loggedIn, user]);

  useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleWheel = (event) => {
      if (event.ctrlKey) {
        event.preventDefault();
      }
    };

    const handleKeyDown = (event) => {
      const zoomKeys = ["=", "+", "-", "_"];
      if ((event.ctrlKey || event.metaKey) && zoomKeys.includes(event.key)) {
        event.preventDefault();
      }
      if ((event.ctrlKey || event.metaKey) && event.key === "0") {
        event.preventDefault();
      }
    };

    const handleTouchMove = (event) => {
      if (event.scale && event.scale !== 1) {
        event.preventDefault();
      }
      if (event.touches && event.touches?.length > 1) {
        event.preventDefault();
      }
    };

    let lastTouchEnd = 0;
    const handleTouchEnd = (event) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        event.preventDefault();
      }
      lastTouchEnd = now;
    };

    const preventGesture = (event) => {
      event.preventDefault();
    };

    document.addEventListener("wheel", handleWheel, { passive: false });
    document.addEventListener("keydown", handleKeyDown);
    document.addEventListener("touchmove", handleTouchMove, { passive: false });
    document.addEventListener("touchend", handleTouchEnd, { passive: false });
    window.addEventListener("gesturestart", preventGesture, { passive: false });
    window.addEventListener("gesturechange", preventGesture, {
      passive: false,
    });
    window.addEventListener("gestureend", preventGesture, { passive: false });

    return () => {
      document.removeEventListener("wheel", handleWheel);
      document.removeEventListener("keydown", handleKeyDown);
      document.removeEventListener("touchmove", handleTouchMove);
      document.removeEventListener("touchend", handleTouchEnd);
      window.removeEventListener("gesturestart", preventGesture);
      window.removeEventListener("gesturechange", preventGesture);
      window.removeEventListener("gestureend", preventGesture);
    };
  }, []);

  // useEffect(() => { ???
  //   const params = new URLSearchParams(location.search);
  //   const ref = params.get ("ref");

  //   if (!loggedIn) {
  //     if (ref) {
  //       dispatch(setAffiliateRef(ref));
  //       localStorage.setItem("affiliateRef", ref);

  //       dispatch(setSignUpModalOpen(true));
  //     } else {
  //       const savedRef = localStorage.getItem("affiliateRef");
  //       if (savedRef) {
  //         dispatch(setAffiliateRef(savedRef));
  //       } else {
  //         dispatch(setSignUpModalOpen(false));
  //         dispatch(setAffiliateRef(null));
  //       }
  //     }
  //   } else {
  //     if (ref) {
  //       params.delete("ref");
  //       navigate({ search: params.toString() }, { replace: true });

  //       dispatch(setAffiliateRef(null));
  //       localStorage.removeItem("affiliateRef");
  //     }
  //   }
  // }, [dispatch, location.pathname, location.search, loggedIn, navigate]);
  return (
    <Fragment>
      <GlobalStyles />

      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/blog" element={<SeoArticlesPage />} />
        {landingPages.map((page) => (
          <Route
            key={page.path}
            path={page.path}
            element={<SeoLandingPage page={page} />}
          />
        ))}
        {seoArticles.map((article) => (
          <Route
            key={article.path}
            path={article.path}
            element={<SeoArticlePage article={article} />}
          />
        ))}
        <Route path="/feed" element={<Navigate to="/" replace />} />
        <Route path="/mine" element={<CollectionPage />} />
        <Route path="/template/:slug" element={<CollectionDetailsPage />} />
        <Route
          path="/subscription"
          element={
            <ProtectedRoute>
              <SubscriptionPage />
            </ProtectedRoute>
          }
        />
        <Route path="/verify-email/:tokenId" element={<VerifyEmailPage />} />
        <Route path="/account/delete" element={<DeleteAccountPage />} />
        <Route path="/terms-and-conditions" element={<TermsPage />} />
        <Route path="/privacy-policy" element={<PrivacyPage />} />
        <Route
          path="/telegram-payment-guide"
          element={<TelegramPaymentGuidePage />}
        />

        {/* Protected Routes */}
        <Route
          path="/referral/earnings"
          element={
            <ProtectedRoute>
              <ReferralEarningsPage />
            </ProtectedRoute>
          }
        />
        <Route
          path="/account/delete"
          element={
            <ProtectedRoute>
              <DeleteAccountPage />
            </ProtectedRoute>
          }
        />
      </Routes>

      {createPortal(
        <Toaster
          position="bottom-right"
          closeButton={false}
          theme={theme}
          duration={3800}
          gap={10}
          toastOptions={{
            classNames: {
              toast: "fapify-toast",
            },
          }}
        />,
        document.body,
      )}
    </Fragment>
  );
};

function App() {
  return (
    <ThemeProvider>
      <GoogleOAuthProvider clientId="244860564802-bvl6sauvcvnfj3c65hutd7g4qvmo6gj9.apps.googleusercontent.com">
        <AppContent />
      </GoogleOAuthProvider>
    </ThemeProvider>
  );
}

export default App;
